import { apiDelete, apiGet, apiPost, apiPut } from '../../generic/Api_Functions'
import SmartMateriaUITable from '../../generic/SmartMateriaUITable'
import { TextField, IconButton, Stack, CircularProgress } from '@mui/material'
import { ManageSearchOutlined, TextSnippetOutlined, PrecisionManufacturingOutlined } from '@mui/icons-material'
import AddRawMaterialElement from '../../generic/smartAddElement/AddRawMaterialElement'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import React, { useState, useRef } from 'react'
import RawMaterial from './RawMaterial'
import MachineStorageDialog from './MachineStorageDialog'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import Document from '../document/Document'
import { useAuthorization } from '../../generic/hooks/useAuthorization'
import useGetExtendedColumns from '../../generic/hooks/useGetExtendedColumns/useGetExtendedColumns'
import useRepositorySelection from '../../generic/hooks/useRepositorySelection'
import { parentPreselectedRecordFrom, childPreselectedRecordFrom, childPreselectedRecordForRow } from '../../generic/utilities/repositorySelectionMapping'
const RawMaterialReference = () => {
  const parallelepiped = 'PARALLELEPIPED'
  const cylindrical = 'CYLINDRICAL'

  const [showRawMaterials, setShowRawMaterials] = useState(false)
  const [referenceForInstances, setReferenceForInstances] = useState(null)
  const [hideColumn, setHideColumn] = useState(false)
  const [editingRow, setEditingRow] = useState(null)
  const { t_ } = useSmartTranslation()
  const [quantityValue, setQuantityValue] = useState(0)
  const [editing, setEditing] = useState(false)
  const disableRowActions = useRef(false)
  const [rawMaterialReferenceRow, setRawMaterialReferenceRow] = useState(null)
  const [showDocument, setShowDocument] = useState(false)
  const [isDownloadingFile, setIsDownloadingFile] = useState(false)
  const [showMachineStorage, setShowMachineStorage] = useState(false)
  const [referenceForMachineStorage, setReferenceForMachineStorage] = useState({})
  const isUserAuthorized = useAuthorization()
  const extendedColumns = useGetExtendedColumns('raw_material_reference')
  // Historia 170 (J4b, S89/S103): RAW_MATERIAL_REFERENCE -> (child) RAW_MATERIAL.
  const repositorySelection = useRepositorySelection()
  const handleRawMaterialReferencePreselectionResolved = (row) => {
    if (row && childPreselectedRecordFrom(repositorySelection)) {
      setReferenceForInstances(row)
      setShowRawMaterials(true)
    }
  }

  const columns = [{
    name: t_('Code'),
    field: 'code',
    readOnly: true,
    charLimit: 60,
    mandatory: true,
    unique: true
  },
  {
    name: t_('Name'),
    field: 'name',
    charLimit: 80,
    mandatory: true,
    readOnly: (row) => {
      return row.systemDefault
    }
  },
  {
    name: t_('Type'),
    field: 'type.code',
    type: 'select',
    defaultNewValue: { code: 'RAW', id: 1 },
    edit: {
      source: async () => {
        return await apiGet('rawMaterialReferenceTypes')
      },
      id: 'id',
      field: 'code'
    },
    mandatory: true
  },
  {
    name: t_('Description'),
    field: 'description',
    charLimit: 200
  }, {
    name: t_('Material type'),
    field: 'materialType.name',
    type: 'select',
    edit: {
      source: async () => {
        return await apiGet('materialTypes')
      },
      id: 'id',
      field: 'name'
    },
    mandatory: true,
    readOnly: (row) => {
      return row.systemDefault
    }
  },
  {
    name: t_('Suppliers'),
    field: 'suppliers.name',
    type: 'smartMultiSelect',
    edit: {
      source: async () => {
        return await apiGet('suppliersBasicData')
      },
      id: 'id',
      field: 'name'
    },
    readOnly: (row) => {
      return row.systemDefault
    }
  },
  {
    name: t_('Shape'),
    field: 'shape.code',
    type: 'select',
    edit: {
      source: async () => {
        return await apiGet('shapes')
      },
      id: 'id',
      field: 'name'
    },
    readOnly: (row) => {
      return row.systemDefault
    }
  },
  {
    name: t_('X Length (mm)'),
    field: 'lengthX',
    showWhenColumnVisible: 'shape.code',
    readOnly: (rowData) => {
      return readOnlyCondition(rowData, parallelepiped) || rowData.systemDefault
    },
    mandatory: (rowData) => {
      return mandatoryCondition(rowData, parallelepiped)
    },
    reset: (rowData) => {
      return readOnlyCondition(rowData, parallelepiped)
    },
    numeric: true
  },
  {
    name: t_('Y Length (mm)'),
    field: 'lengthY',
    showWhenColumnVisible: 'shape.code',
    readOnly: (rowData) => {
      return readOnlyCondition(rowData, parallelepiped) || rowData.systemDefault
    },
    mandatory: (rowData) => {
      return mandatoryCondition(rowData, parallelepiped)
    },
    reset: (rowData) => {
      return readOnlyCondition(rowData, parallelepiped)
    },
    numeric: true
  },
  {
    name: t_('Z Length (mm)'),
    field: 'lengthZ',
    showWhenColumnVisible: 'shape.code',
    readOnly: (rowData) => {
      return readOnlyCondition(rowData, parallelepiped) || rowData.systemDefault
    },
    mandatory: (rowData) => {
      return mandatoryCondition(rowData, parallelepiped)
    },
    reset: (rowData) => {
      return readOnlyCondition(rowData, parallelepiped)
    },
    numeric: true
  },

  {
    name: t_('Diam (mm)'),
    field: 'diameter',
    showWhenColumnVisible: 'shape.code',
    readOnly: (rowData) => {
      return readOnlyCondition(rowData, cylindrical) || rowData.systemDefault
    },
    mandatory: (rowData) => {
      return mandatoryCondition(rowData, cylindrical)
    },
    reset: (rowData) => {
      return readOnlyCondition(rowData, cylindrical)
    },
    numeric: true
  },

  {
    name: t_('Length (mm)'),
    field: 'length',
    showWhenColumnVisible: 'shape.code',
    readOnly: (rowData) => {
      return readOnlyCondition(rowData, cylindrical) || rowData.systemDefault
    },
    mandatory: (rowData) => {
      return mandatoryCondition(rowData, cylindrical)
    },
    reset: (rowData) => {
      return readOnlyCondition(rowData, cylindrical)
    },
    numeric: {
      default: 0
    }
  },
  {
    name: t_('Fixture'),
    field: 'defaultFixtureReference.code',
    type: 'select',
    edit: {
      source: async () => {
        if (editingRow !== null) { return await apiGet('fixtureReferencesByRawMaterialReference/' + editingRow.id) }
      },
      id: 'id',
      field: 'code'
    },
    hide: hideColumn,
    readOnly: (row) => {
      return row.systemDefault
    }
  },

  {
    name: t_('Safety stock'),
    field: 'safetyStock',
    numeric: true,
    readOnly: (row) => {
      return row.systemDefault
    },
    condition: (row) => {
      if (row.safetyStock < 0) {
        return t_('Must be positive')
      }
    }
  },
  {
    name: t_('Minimum manufacturing batch'),
    field: 'minimumManufacturingBatch',
    numeric: true,
    readOnly: (row) => {
      return row.systemDefault
    }
  },
  {
    // S134 (CA12, RN33, D8): opts the reference into minimum-stock monitoring. Mirror of
    // `part_reference` (#45), in fields of its own — `safetyStock` above is NOT reused and keeps
    // sizing the child fabrication orders.
    // Born off (RN34, S115): no reference is ever evaluated until someone turns it on, so updating
    // an existing installation is regression-free. Turning it off does NOT clear the threshold.
    name: t_('Minimum stock controlled'),
    field: 'minimumStockControlled',
    type: 'boolean',
    defaultNewValue: false,
    readOnly: (row) => {
      return row.systemDefault
    }
  },
  {
    // S134: the threshold the low-stock condition is measured against (RN35, inclusive in favour of
    // normality: available === minimum is NOT low stock). Travels inside the whole row of the
    // existing apiPut/apiPost — no new endpoint.
    name: t_('Minimum stock'),
    field: 'minimumStock',
    numeric: true,
    readOnly: (row) => {
      return row.systemDefault
    },
    // E8: required exactly while the control is on. Row-dependent, which the generic table supports.
    mandatory: (row) => {
      return Boolean(row && row.minimumStockControlled)
    },
    condition: (row) => {
      return checkMinimumStockCondition(row)
    }
  },
  {
    name: t_('Lead time'),
    field: 'leadTime',
    numeric: true,
    readOnly: (row) => {
      return row.systemDefault
    }
  },
  {
    name: t_('Size'),
    field: 'size',
    numeric: true,
    decimals: 2,
    readOnly: (row) => {
      return row.systemDefault || !row.fractionable
    },
    mandatory: (row) => {
      return row.fractionable
    },
    condition: (row) => {
      if (!row.size && row.fractionable) {
        return t_('Can not be empty')
      }

      if (row.size < 1 && row.fractionable) {
        return t_('Must be positive')
      }
    },
    reset: (row) => {
      return !row.fractionable
    }
  },
  {
    name: t_('Unit'),
    field: 'unit.code',
    type: 'smartSelect',
    edit: {
      source: async () => {
        return await apiGet('units')
      },
      id: 'id',
      field: 'code'
    },
    readOnly: (row) => {
      return row.systemDefault || !row.fractionable
    },
    reset: (row) => {
      return !row.fractionable
    }
  },
  {
    name: t_('Quantity (avail./total)'),
    field: 'quantity',
    numeric: true,
    defaultNewValue: 0,
    sortable: false,
    search: false,
    mandatory: true,
    disableFilter: true,
    render: (row, handleExtraFunctions) => {
      return (
        row !== undefined && row.id === undefined && row.quantity === 0
          ? <TextField
              value={quantityValue}
              onChange={(event) => {
                setQuantityValue(event.target.value)
              }}
              helperText={' '}
              type='number'
              disabled={!isUserAuthorized('ADD_RAW_MATERIAL')}
              inputProps={{ min: 0 }}
            />
          : (
            <Stack
              direction='row'
              justifyContent='center'
              alignItems='center'
            >
              {row.availableQuantity}/{row.quantity}
              <AddRawMaterialElement
                row={row}
                disable={editing || disableRowActions.current || !isUserAuthorized('ADD_RAW_MATERIAL')}
                onAlert={handleExtraFunctions.showAlert}
              />
            </Stack>)
      )
    }
  },
  {
    name: t_('Quantity available second unit'),
    field: 'quantityAvailableSecondUnit',
    readOnly: true,
    hide: hideColumn
  },
  {
    name: t_('Serializable'),
    field: 'serializable',
    type: 'boolean',
    defaultNewValue: false,
    readOnly: (row) => {
      return row.systemDefault
    }
  },
  {
    name: t_('Fractionable'),
    field: 'fractionable',
    type: 'boolean',
    defaultNewValue: false,
    readOnly: (row) => {
      return row.systemDefault || row.quantity > 0
    }
  }, {
    name: t_('Unitary'),
    field: 'unitary',
    type: 'boolean',
    defaultNewValue: true,
    readOnly: (row) => {
      return row.systemDefault || row.quantity > 0
    }
  }
  ]

  const getColumns = () => {
    if (extendedColumns?.length === 0) {
      return columns
    }
    return columns.concat(extendedColumns)
  }

  const onEditIndexChanged = (editIndex, row) => {
    setHideColumn(false)
    if (editIndex === -1) {
      // Creating new element
      setHideColumn(true)
    }

    if (editIndex >= 0) {
      // Editing element. Row will have data
      setEditingRow(row)
      setHideColumn(false)
    }
  }

  // S135 (E7, E8). Duplicates the server's validation to give an immediate answer; it does NOT
  // replace it. E7: numeric and >= 0 — decimals are legitimate, raw material quantities are not whole
  // units, so the `>= 1 integer` rule of the part_reference mirror (#45) does NOT apply here.
  // E8: turning the control on requires the threshold to be informed. Turning it off keeps it.
  const checkMinimumStockCondition = (row) => {
    const value = row ? row.minimumStock : ''
    const isEmpty = value === undefined || value === null || value === ''

    if (row && row.minimumStockControlled && isEmpty) {
      return t_('Minimum stock is required when minimum stock control is enabled')
    }

    if (!isEmpty && (!Number.isFinite(Number(value)) || Number(value) < 0)) {
      return t_('Minimum stock must be a number greater than or equal to 0')
    }

    return ''
  }

  // Blocks the save BEFORE any API round-trip: the column `condition` alone is only re-evaluated when
  // the column's own value changes, so ticking the flag while leaving the field empty would otherwise
  // reach the backend and bounce with a raw error (same lesson as the #45 mirror).
  const validator = (row) => {
    const errorMsg = checkMinimumStockCondition(row)
    if (errorMsg !== '') {
      return { errorFields: ['minimumStock'], alertMessage: errorMsg }
    }
    return null
  }

  const mandatoryCondition = (row, property) => {
    if (!row.shape) {
      return false
    }
    if (row.shape.code === property) return true
  }

  const readOnlyCondition = (row, property) => {
    if (
      !row.shape ||
      row.shape.code !== property ||
      row.shape.code === '' ||
      row.shape.code === undefined
    ) { return true }
    return false
  }

  const actions = {
    edit: {
      disabled: !isUserAuthorized('EDIT_RAW_MATERIAL_REFERENCE'),
      fetchFunction: (row) => {
        if (row && row.size !== undefined && row.size !== null) {
          const v = row.size
          const num = typeof v === 'string' ? parseFloat(String(v).replace(',', '.')) : v
          let normalized = Number.isFinite(num) ? num : 0
          if (normalized < 0) normalized = 0
          row.size = Number(normalized.toFixed(2))
        }
        return apiPut('rawMaterialsReference', row)
      }
    },
    create: {
      disabled: !isUserAuthorized('ADD_RAW_MATERIAL_REFERENCE'),
      fetchFunction: (row) => {
        if (!row.suppliers || (typeof row.suppliers === 'object' && !Array.isArray(row.suppliers)) || row.suppliers.length === 0) {
          row.suppliers = []
        }
        if (Object.keys(row.materialType).length === 0) row.materialType = null
        if (row && row.size !== undefined && row.size !== null) {
          const v = row.size
          const num = typeof v === 'string' ? parseFloat(String(v).replace(',', '.')) : v
          let normalized = Number.isFinite(num) ? num : 0
          if (normalized < 0) normalized = 0
          row.size = Number(normalized.toFixed(2))
        }
        row.quantity = quantityValue
        setQuantityValue(0)
        return apiPost('rawMaterialsReference', row)
      }
    },
    delete: {
      disabled: !isUserAuthorized('DELETE_RAW_MATERIAL_REFERENCE'),
      fetchFunction: (rows) => {
        if (rows.length === 1) {
          return apiDelete('rawMaterialsReference/' + rows[0].id)
        } else {
          return apiDelete('rawMaterialsReferenceMulti', rows)
        }
      },
      visibilityButton: (row) => {
        return !row.systemDefault
      }
    },
    custom: [
      {
        name: 'Documents',
        render: (row) => {
          return (
            <IconButton>
              <TextSnippetOutlined />
            </IconButton>
          )
        },
        disabled: editing || !isUserAuthorized('DOCUMENTS_RAW_MATERIAL_REFERENCE'),
        onClick: (row) => {
          setRawMaterialReferenceRow(row)
          setShowDocument(true)
        }
      },
      {
        name: 'View Raw Materials',
        render: (row) => {
          return (
            <IconButton>
              <ManageSearchOutlined />
            </IconButton>
          )
        },
        disabled: !!editing || !isUserAuthorized('RAW_MATERIAL_INSTANCES'),
        onClick: (row) => {
          setReferenceForInstances(row)
          setShowRawMaterials(true)
        }
      },
      {
        name: 'Machine storage',
        render: (row) => {
          return (
            <IconButton>
              <PrecisionManufacturingOutlined />
            </IconButton>
          )
        },
        disabled: !!editing || !isUserAuthorized('ADD_RAW_MATERIAL'),
        onClick: (row) => {
          setReferenceForMachineStorage(row)
          setShowMachineStorage(true)
        }
      }
    ]
  }

  if (extendedColumns === null || extendedColumns === undefined) {
    return <CircularProgress />
  }

  return (
    <div>
      <SmartDialog
        closeCallback={() => setShowDocument(false)}
        accept={false}
        cancel={false}
        close
        renderComponent={<Document reference={rawMaterialReferenceRow} resourceType='RAW_MATERIAL_REFERENCE' isDownloadingFile={isDownloadingFile} setIsDownloadingFile={setIsDownloadingFile} />}
        setOpen={showDocument}
      />
      <SmartDialog
        renderComponent={
          <RawMaterial
            reference={referenceForInstances}
            endpoint='rawMaterials'
            showQrAction
            preselectedRecord={childPreselectedRecordForRow(repositorySelection, referenceForInstances?.id)}
          />
        }
        closeCallback={() => setShowRawMaterials(false)}
        cancel={false}
        accept={false}
        close
        setOpen={showRawMaterials}
      />
      <MachineStorageDialog
        open={showMachineStorage}
        reference={referenceForMachineStorage}
        onClose={() => setShowMachineStorage(false)}
      />
      <SmartMateriaUITable
        tableTuneUniqueKey='rawMaterialReference'
        columns={getColumns()}
        title={t_('Raw Materials')}
        dataFetch='rawMaterialsReference/paginated'
        actions={actions}
        multipleSelection
        deleteMultipleSelectionDisabled={!isUserAuthorized('DELETE_RAW_MATERIAL_REFERENCE')}
        editingRow={(event) => setEditing(event)}
        setDisableRowActions={(event) => disableRowActions.current === event}
        importFile={[
          {
            bean: {
              beanName: 'ImportRawMaterialReference',
              beanPackage: 'com.smartpm.sun.usecase.rawmaterialreference.infrastructure.importer',
              serviceName: 'ImportRawMaterialReferenceService',
              servicePackage: 'com.smartpm.sun.usecase.rawmaterialreference.infrastructure.importer'
            },
            name: t_('Raw Material Reference')
          },
          {
            bean: {
              beanName: 'ImportRawMaterial',
              beanPackage: 'com.smartpm.sun.usecase.rawmaterial.infrastructure.importer',
              serviceName: 'ImportRawMaterialService',
              servicePackage: 'com.smartpm.sun.usecase.rawmaterial.infrastructure.importer'
            },
            name: t_('Raw Material')
          }
        ]}
        importFileDisabled={!isUserAuthorized('IMPORT_FILE_RAW_MATERIAL_REFERENCE')}
        onEditIndexChanged={onEditIndexChanged}
        validator={validator}
        pageable
        preselectedRecord={parentPreselectedRecordFrom(repositorySelection)}
        onPreselectionResolved={handleRawMaterialReferencePreselectionResolved}
      />
    </div>
  )
}

export default RawMaterialReference
